(function () {
  'use strict';

  var elementName = 'purchase-interactive-video-color-switch';

  if (customElements.get(elementName)) {
    return;
  }

  class PurchaseInteractiveVideoColorSwitch extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.activeColorId = null;
      this.activePanel = null;
      this.activeSegmentIndex = -1;
      this.isInitialFrame = true;
      this.isPlaying = false;
      this.segmentMonitorRaf = null;
      this.videoListeners = [];
      this.mediaQuery = window.matchMedia('(max-width: 749px)');
      this.handleClick = this.handleClick.bind(this);
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handleThemeEditorBlockSelect = this.handleThemeEditorBlockSelect.bind(this);
      this.handleThemeEditorSectionLoad = this.handleThemeEditorSectionLoad.bind(this);
      this.handleVideoEnded = this.handleVideoEnded.bind(this);
      this.handleVideoError = this.handleVideoError.bind(this);
      this.handleVideoTimeUpdate = this.handleVideoTimeUpdate.bind(this);

      this.colorButtons = Array.from(this.querySelectorAll('[data-color-target]'));
      this.panels = Array.from(this.querySelectorAll('[data-color-panel]'));
      this.videos = Array.from(this.querySelectorAll('.purchase-interactive-video-color-switch__video'));

      this.addEventListener('click', this.handleClick);
      this.videos.forEach((video) => {
        video.addEventListener('ended', this.handleVideoEnded);
        video.addEventListener('error', this.handleVideoError);
        if (video.dataset.masterVideo === 'true') {
          video.addEventListener('timeupdate', this.handleVideoTimeUpdate);
        }
        this.videoListeners.push(video);
      });

      if (this.mediaQuery.addEventListener) {
        this.mediaQuery.addEventListener('change', this.handleMediaChange);
      } else {
        this.mediaQuery.addListener(this.handleMediaChange);
      }

      document.addEventListener('shopify:block:select', this.handleThemeEditorBlockSelect);
      document.addEventListener('shopify:section:load', this.handleThemeEditorSectionLoad);

      var firstButton = this.colorButtons[0];
      if (firstButton) {
        this.activateColor(firstButton.getAttribute('data-color-target'));
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('shopify:block:select', this.handleThemeEditorBlockSelect);
      document.removeEventListener('shopify:section:load', this.handleThemeEditorSectionLoad);

      if (this.mediaQuery) {
        if (this.mediaQuery.removeEventListener) {
          this.mediaQuery.removeEventListener('change', this.handleMediaChange);
        } else {
          this.mediaQuery.removeListener(this.handleMediaChange);
        }
      }

      this.videoListeners.forEach((video) => {
        video.removeEventListener('ended', this.handleVideoEnded);
        video.removeEventListener('error', this.handleVideoError);
        if (video.dataset.masterVideo === 'true') {
          video.removeEventListener('timeupdate', this.handleVideoTimeUpdate);
        }
        this.stopVideo(video);
      });
      this.cancelSegmentMonitor();
      this.videoListeners = [];
      this.activePanel = null;
      this.initialized = false;
    }

    handleClick(event) {
      var colorButton = event.target.closest('[data-color-target]');
      if (colorButton && this.contains(colorButton)) {
        event.preventDefault();
        this.activateColor(colorButton.getAttribute('data-color-target'));
        return;
      }

      var trigger = event.target.closest('[data-play-segment]');
      if (trigger && this.contains(trigger)) {
        event.preventDefault();
        var segmentIndex = parseInt(trigger.getAttribute('data-play-segment'), 10);
        if (!Number.isNaN(segmentIndex)) {
          this.playSegment(segmentIndex);
        }
      }
    }

    handleThemeEditorBlockSelect(event) {
      if (!this.contains(event.target)) return;

      var panel = event.target.closest('[data-color-panel]');
      if (!panel || !this.contains(panel)) return;

      this.activateColor(panel.getAttribute('data-color-panel'));
    }

    handleThemeEditorSectionLoad(event) {
      if (!event.target || (event.target !== this && !event.target.contains(this))) return;
      var firstButton = this.colorButtons[0];
      if (!this.activePanel && firstButton) {
        this.activateColor(firstButton.getAttribute('data-color-target'));
      }
    }

    handleMediaChange() {
      if (!this.activePanel) return;
      this.stopAllVideos();
      this.isPlaying = false;
      this.activeSegmentIndex = -1;
      this.isInitialFrame = true;
      this.renderPanelState();
    }

    handleVideoEnded(event) {
      if (!this.activePanel || event.target !== this.getActiveVideo(this.activePanel, this.activeSegmentIndex)) {
        return;
      }

      if (!this.isPlaying) return;
      this.finishMasterSegment(event.target, event.target.duration);
    }

    handleVideoTimeUpdate(event) {
      if (!this.activePanel || !this.isPlaying) {
        return;
      }

      if (event.target !== this.getActiveVideo(this.activePanel, this.activeSegmentIndex)) {
        return;
      }

      var range = this.getMasterSegmentRange(this.activePanel, this.activeSegmentIndex, event.target);
      if (range && range.end > 0 && event.target.currentTime >= range.end - 0.008) {
        this.finishMasterSegment(event.target, range.end);
      }
    }

    monitorSegment(video) {
      this.cancelSegmentMonitor();
      var check = function () {
        if (!this.isPlaying || !this.activePanel || this.getActiveVideo(this.activePanel, this.activeSegmentIndex) !== video) {
          this.segmentMonitorRaf = null;
          return;
        }

        var range = this.getMasterSegmentRange(this.activePanel, this.activeSegmentIndex, video);
        if (range && range.end > 0 && video.currentTime >= range.end - 0.008) {
          this.segmentMonitorRaf = null;
          this.finishMasterSegment(video, range.end);
          return;
        }

        this.segmentMonitorRaf = window.requestAnimationFrame(check);
      }.bind(this);

      this.segmentMonitorRaf = window.requestAnimationFrame(check);
    }

    cancelSegmentMonitor() {
      if (this.segmentMonitorRaf) {
        window.cancelAnimationFrame(this.segmentMonitorRaf);
        this.segmentMonitorRaf = null;
      }
    }

    finishMasterSegment(video, boundaryTime) {
      if (!this.activePanel || !video || !this.isPlaying) return;

      this.cancelSegmentMonitor();
      this.isPlaying = false;

      var currentIndex = this.activeSegmentIndex;
      var nextIndex = this.getAvailableSegments(this.activePanel).find(function (index) {
        return index > currentIndex && this.getMasterSegmentRange(this.activePanel, index, video);
      }, this);

      if (typeof nextIndex === 'number') {
        this.activeSegmentIndex = nextIndex;
        this.isInitialFrame = false;
        var nextRange = this.getMasterSegmentRange(this.activePanel, nextIndex, video);
        this.seekVideo(video, nextRange ? nextRange.start : 0);
      } else {
        var firstIndex = this.getAvailableSegments(this.activePanel)[0];
        this.activeSegmentIndex = typeof firstIndex === 'number' ? firstIndex : -1;
        this.isInitialFrame = true;
        this.seekVideo(video, 0);
      }

      if (typeof boundaryTime === 'number' && Number.isFinite(boundaryTime)) {
        video.pause();
      }

      this.renderPanelState();
    }

    handleVideoError(event) {
      if (!this.activePanel || event.target !== this.getActiveVideo(this.activePanel, this.activeSegmentIndex)) {
        return;
      }

      this.stopVideo(event.target);
      this.isPlaying = false;
      this.activeSegmentIndex = -1;
      this.isInitialFrame = true;
      this.renderPanelState();
    }

    activateColor(colorId) {
      var panel = this.querySelector('[data-color-panel="' + colorId + '"]');
      if (!panel) return;

      this.stopAllVideos();
      this.activeColorId = colorId;
      this.activePanel = panel;
      this.activeSegmentIndex = -1;
      this.isInitialFrame = true;
      this.isPlaying = false;

      this.panels.forEach(function (item) {
        var isActive = item === panel;
        item.classList.toggle('is-active', isActive);
        item.hidden = !isActive;
      });

      this.colorButtons.forEach(function (button) {
        var isActive = button.getAttribute('data-color-target') === colorId;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this.renderPanelState();
    }

    playSegment(segmentIndex) {
      if (!this.activePanel || this.isPlaying) return;

      var video = this.getActiveVideo(this.activePanel, segmentIndex);
      if (!video) return;

      var masterRange = this.getMasterSegmentRange(this.activePanel, segmentIndex, video);
      if (!masterRange) return;

      this.stopAllVideos(false);
      this.activeSegmentIndex = segmentIndex;
      this.isInitialFrame = false;
      this.isPlaying = true;
      this.renderPanelState();

      try {
        video.currentTime = masterRange ? masterRange.start : 0;
      } catch (error) {
        this.isPlaying = false;
        this.isInitialFrame = true;
        this.renderPanelState();
        return;
      }

      var playPromise = video.play();
      this.monitorSegment(video);
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          this.cancelSegmentMonitor();
          this.stopVideo(video);
          this.isPlaying = false;
          this.activeSegmentIndex = -1;
          this.isInitialFrame = true;
          this.renderPanelState();
        });
      }
    }

    renderPanelState() {
      if (!this.activePanel) return;

      var currentIndex = this.activeSegmentIndex;
      var currentVideo = currentIndex >= 0 ? this.getActiveVideo(this.activePanel, currentIndex) : null;
      var triggerIndex = currentIndex;

      if (currentIndex < 0) {
        triggerIndex = this.getAvailableSegments(this.activePanel)[0];
      }

      this.activePanel.classList.toggle('is-playing', this.isPlaying);
      this.activePanel.classList.toggle('has-segment-preview', !this.isPlaying && currentIndex >= 0);
      this.activePanel.dataset.activeSegment = currentIndex < 0 ? 'initial' : String(currentIndex);

      this.activePanel.querySelectorAll('.purchase-interactive-video-color-switch__video').forEach(function (video) {
        var isCurrent = video === currentVideo;
        video.classList.toggle('is-active', isCurrent);
        video.setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
      });

      var copy = this.activePanel.querySelector('.purchase-interactive-video-color-switch__copy');
      if (copy) {
        copy.dataset.copyState = this.isInitialFrame || currentIndex < 0 ? 'initial' : 'segment';
        var initialCopy = copy.querySelector('.purchase-interactive-video-color-switch__copy-item--initial');
        if (initialCopy) initialCopy.hidden = !this.isInitialFrame;
        copy.querySelectorAll('[data-segment-copy]').forEach(function (item) {
          item.hidden = this.isInitialFrame || parseInt(item.getAttribute('data-segment-copy'), 10) !== currentIndex;
        }, this);
      }

      this.activePanel.querySelectorAll('[data-play-segment]').forEach(function (button) {
        var isVisible = !this.isPlaying && parseInt(button.getAttribute('data-play-segment'), 10) === triggerIndex;
        button.hidden = !isVisible;
        button.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
      }, this);
    }

    getAvailableSegments(panel) {
      return Array.from(panel.querySelectorAll('[data-play-segment]'))
        .map(function (button) {
          return parseInt(button.getAttribute('data-play-segment'), 10);
        })
        .filter(function (index) {
          return !Number.isNaN(index);
        })
        .sort(function (a, b) {
          return a - b;
        });
    }

    getMasterSegmentRange(panel, segmentIndex, video) {
      if (!panel || segmentIndex < 0) return null;

      var buttons = Array.from(panel.querySelectorAll('[data-play-segment]')).sort(function (a, b) {
        return parseInt(a.getAttribute('data-play-segment'), 10) - parseInt(b.getAttribute('data-play-segment'), 10);
      });
      var button = buttons.find(function (item) {
        return parseInt(item.getAttribute('data-play-segment'), 10) === segmentIndex;
      });
      if (!button) return null;

      var previousButton = buttons.filter(function (item) {
        return parseInt(item.getAttribute('data-play-segment'), 10) < segmentIndex;
      }).pop();
      var start = previousButton ? Number(previousButton.dataset.segmentEnd) : 0;
      var end = Number(button.dataset.segmentEnd);
      var duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
      var lastIndex = buttons.length ? parseInt(buttons[buttons.length - 1].getAttribute('data-play-segment'), 10) : -1;

      start = Number.isFinite(start) && start > 0 ? start : 0;
      end = Number.isFinite(end) && end > start ? end : segmentIndex === lastIndex ? duration : 0;
      if (duration > 0) {
        start = Math.min(start, duration);
        end = Math.min(end, duration);
      }

      return end > start ? { start: start, end: end } : null;
    }

    seekVideo(video, time) {
      if (!video || !Number.isFinite(time)) return;
      video.pause();
      try {
        video.currentTime = Math.max(0, time);
      } catch (error) {
        // 某些浏览器在媒体元数据尚未加载时不允许设置 currentTime。
      }
    }

    getActiveVideo(panel, segmentIndex) {
      if (!panel || segmentIndex < 0) return null;

      var mobileMasterVideo = panel.querySelector('.purchase-interactive-video-color-switch__video--master.purchase-interactive-video-color-switch__video--mobile');
      var desktopMasterVideo = panel.querySelector('.purchase-interactive-video-color-switch__video--master.purchase-interactive-video-color-switch__video--desktop');
      return this.mediaQuery.matches ? mobileMasterVideo || desktopMasterVideo : desktopMasterVideo || mobileMasterVideo;
    }

    stopAllVideos(resetTime) {
      this.cancelSegmentMonitor();
      var shouldResetTime = resetTime !== false;
      this.videos.forEach(function (video) {
        this.stopVideo(video, shouldResetTime);
      }, this);
    }

    stopVideo(video, resetTime) {
      if (!video) return;
      video.pause();
      if (resetTime !== false) {
        try {
          video.currentTime = 0;
        } catch (error) {
          // 某些浏览器在媒体元数据尚未加载时不允许设置 currentTime。
        }
      }
      video.classList.remove('is-active');
    }
  }

  customElements.define(elementName, PurchaseInteractiveVideoColorSwitch);
})();
